<template>
	<div class="view-appointment">
		<Modal
			:show.sync="showAppointmentData"
			content_class="w-full lg:w-10/12 xl:w-9/12 confirm-modal"
		>
			<template #content>
				<div
					class="bg-white rounded-2xl shadow-2xl overflow-hidden"
				>
					<!-- Header -->
					<div
						class="bg-primary px-6 sm:px-8 py-4 sm:py-6 flex items-center justify-between border-b border-primary/20"
					>
						<div class="flex items-center space-x-3">
							<div
								class="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center"
							>
								<svg
									class="w-5 h-5 sm:w-6 sm:h-6 text-white"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
									/>
								</svg>
							</div>
							<h1 class="text-xl sm:text-2xl lg:text-3xl font-bold text-white">
								Appointment Details
							</h1>
						</div>
						<button
							class="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30 text-white transition-all duration-200 hover:rotate-90"
							@click="$store.commit('showAppointmentData', false)"
						>
							<i class="fa fa-times text-lg sm:text-xl"></i>
						</button>
					</div>

					<!-- Content -->
					<div class="p-4 sm:p-6 lg:p-8 space-y-6">
						<!-- Title Section -->
						<div
							class="bg-primary/5 rounded-xl p-4 sm:p-6 border border-primary/20"
						>
							<div class="flex items-start space-x-3 sm:space-x-4">
								<div
									class="w-10 h-10 bg-primary rounded-lg flex items-center justify-center flex-shrink-0"
								>
									<svg
										class="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
										/>
									</svg>
								</div>
								<div class="flex-1 min-w-0">
									<p
										class="text-xs font-semibold text-primary uppercase tracking-wider mb-1"
									>
										Title
									</p>
									<p
										v-if="showSelectedAppointment"
										class="text-lg sm:text-xl font-bold text-gray-900 truncate"
									>
										{{ showSelectedAppointment.title }}
									</p>
								</div>
							</div>
						</div>

						<!-- Message Section -->
						<div
							class="bg-gray-50 rounded-xl p-4 sm:p-6 border border-gray-200"
						>
							<div class="flex items-start space-x-3 sm:space-x-4">
								<div
									class="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center flex-shrink-0"
								>
									<svg
										class="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
										/>
									</svg>
								</div>
								<div class="flex-1 min-w-0">
									<p
										class="text-xs font-semibold text-gray-600 uppercase tracking-wider mb-2"
									>
										Message
									</p>
									<p
										class="text-sm sm:text-base text-gray-700 leading-relaxed"
									>
										{{ showSelectedAppointment.message }}
									</p>
								</div>
							</div>
						</div>

						<!-- Date and Status Grid -->
						<div class="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
							<!-- Appointment Date -->
							<div
								class="bg-accent/5 rounded-xl p-4 sm:p-6 border border-accent/20"
							>
								<div class="flex items-center space-x-3 mb-3">
									<div
										class="w-10 h-10 bg-accent rounded-lg flex items-center justify-center"
									>
										<svg
											class="w-5 h-5 text-white"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
											/>
										</svg>
									</div>
									<p
										class="text-xs font-semibold text-accent uppercase tracking-wider"
									>
										Appointment Date
									</p>
								</div>
								<p
									class="text-base sm:text-lg font-bold text-gray-800"
								>
									{{
										formatDate(
											showSelectedAppointment.appointmentDate,
										)
									}}
									at
									{{
										showSelectedAppointment.appointmentTime
									}}
								</p>
							</div>

							<!-- Status -->
							<div
								class="bg-white rounded-xl p-6 border-2 border-gray-200"
							>
								<div class="flex items-center space-x-3 mb-2">
									<div
										class="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center"
									>
										<svg
											class="w-5 h-5 text-white"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												stroke-linecap="round"
												stroke-linejoin="round"
												stroke-width="2"
												d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
											/>
										</svg>
									</div>
									<p
										class="text-xs font-semibold text-gray-600 uppercase tracking-wider"
									>
										Status
									</p>
								</div>
								<div class="ml-13">
									<span
										class="inline-flex items-center px-4 py-2 rounded-full text-sm font-bold"
										:class="{
											'text-yellow-700 bg-yellow-100 border border-yellow-300':
												showSelectedAppointment.status ===
												'PENDING',
											'text-green-700 bg-green-100 border border-green-300':
												showSelectedAppointment.status ===
												'ACCEPTED',
											'text-red-700 bg-red-100 border border-red-300':
												showSelectedAppointment.status ===
												'REJECTED',
										}"
									>
										<span
											class="w-2 h-2 rounded-full mr-2"
											:class="{
												'bg-yellow-500':
													showSelectedAppointment.status ===
													'PENDING',
												'bg-green-500':
													showSelectedAppointment.status ===
													'ACCEPTED',
												'bg-red-500':
													showSelectedAppointment.status ===
													'REJECTED',
											}"
										></span>
										{{ showSelectedAppointment.status }}
									</span>
								</div>
							</div>
						</div>

						<!-- Client Details -->
						<div
							class="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 mb-6 border border-cyan-200"
						>
							<div class="flex items-center space-x-3 mb-4">
								<div
									class="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center"
								>
									<svg
										class="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
										/>
									</svg>
								</div>
								<h2 class="text-lg font-bold text-gray-800">
									Client Details
								</h2>
							</div>
							<div class="space-y-3 ml-13">
								<div class="flex items-center">
									<svg
										class="w-5 h-5 text-cyan-600 mr-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
										/>
									</svg>
									<span class="text-gray-800"
										><span class="font-semibold"
											>Name:</span
										>
										{{
											showSelectedAppointment
												.clientDetails.name
										}}</span
									>
								</div>
								<div class="flex items-center">
									<svg
										class="w-5 h-5 text-cyan-600 mr-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
										/>
									</svg>
									<span class="text-gray-800"
										><span class="font-semibold"
											>Email:</span
										>
										{{
											showSelectedAppointment
												.clientDetails.email
										}}</span
									>
								</div>
								<div class="flex items-center">
									<svg
										class="w-5 h-5 text-cyan-600 mr-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
										/>
									</svg>
									<span class="text-gray-800"
										><span class="font-semibold"
											>Phone:</span
										>
										{{
											showSelectedAppointment
												.clientDetails.phone
										}}</span
									>
								</div>
							</div>
						</div>
						<div
							class="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-xl p-6 mb-6 border border-cyan-200"
						>
							<div class="flex items-center space-x-3 mb-4">
								<div
									class="w-10 h-10 bg-cyan-500 rounded-lg flex items-center justify-center"
								>
									<svg
										class="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
										/>
									</svg>
								</div>
								<h2 class="text-lg font-bold text-gray-800">
									Intermediary Details
								</h2>
							</div>
							<div class="space-y-3 ml-13">
								<div class="flex items-center">
									<svg
										class="w-5 h-5 text-cyan-600 mr-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
										/>
									</svg>
									<span class="text-gray-800"
										><span class="font-semibold"
											>Name:</span
										>
										{{
											getIntermediaryData(
												showSelectedAppointment.intermediarySystemRef,
											).name
										}}</span
									>
								</div>
								<div class="flex items-center">
									<svg
										class="w-5 h-5 text-cyan-600 mr-3"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
										/>
									</svg>
									<span class="text-gray-800"
										><span class="font-semibold"
											>Email:</span
										>
										{{
											getIntermediaryData(
												showSelectedAppointment.intermediarySystemRef,
											).email
										}}</span
									>
								</div>
							</div>
						</div>

						<!-- Created Info -->
						<div
							class="bg-gray-100 rounded-xl p-6 mb-6 border border-gray-300"
						>
							<div class="flex items-center space-x-3 mb-3">
								<div
									class="w-10 h-10 bg-gray-600 rounded-lg flex items-center justify-center"
								>
									<svg
										class="w-5 h-5 text-white"
										fill="none"
										stroke="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											stroke-linecap="round"
											stroke-linejoin="round"
											stroke-width="2"
											d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
										/>
									</svg>
								</div>
								<h2 class="text-lg font-bold text-gray-800">
									Additional Information
								</h2>
							</div>
							<div class="space-y-2 ml-13 text-sm">
								<p class="text-gray-600">
									<span class="font-semibold text-gray-700"
										>Created At:</span
									>
									{{
										formatDate(
											showSelectedAppointment.createdAt,
										)
									}}
								</p>
								<p class="text-gray-600">
									<span class="font-semibold text-gray-700"
										>Sent By:</span
									>
									{{ showSelectedAppointment.createdBy }}
								</p>
								<p
									v-if="
										showSelectedAppointment.createdBy ==
										'CLIENT'
									"
									class="text-gray-600"
								>
									<span class="font-semibold text-gray-700"
										>To:</span
									>
									{{
										getIntermediaryData(
											showSelectedAppointment.intermediarySystemRef,
										).name
									}}
								</p>
								<p
									v-if="
										showAppointmentData.createdBy ==
										'INTERMEDIARY'
									"
									class="text-gray-600"
								>
									<span class="font-semibold text-gray-700"
										>To:</span
									>
									{{
										showSelectedAppointment.clientDetails
											.name
									}}
								</p>
							</div>
						</div>

						<!-- Action Buttons -->

						<div
							v-if="
								showSelectedAppointment.createdBy != 'CLIENT' &&
								showSelectedAppointment.status != 'ACCEPTED'
							"
							class="flex flex-col sm:flex-row gap-4 justify-end"
						>
							<button
								class="group relative px-8 py-3 bg-gradient-to-r from-red-600 to-red-700 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
							>
								<svg
									class="w-5 h-5 mr-2 group-hover:rotate-12 transition-transform duration-200"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M6 18L18 6M6 6l12 12"
									/>
								</svg>
								Reject
							</button>
							<button
								@click="handleAcceptAppointment"
								class="group relative px-8 py-3 bg-gradient-to-r from-green-600 to-green-700 rounded-xl text-white font-semibold shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center"
							>
								<svg
									class="w-5 h-5 mr-2 group-hover:scale-110 transition-transform duration-200"
									fill="none"
									stroke="currentColor"
									viewBox="0 0 24 24"
								>
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M5 13l4 4L19 7"
									/>
								</svg>
								Accept
							</button>
						</div>
					</div>
				</div>
			</template>
		</Modal>
	</div>
</template>

<script>
import { mapGetters } from 'vuex';
import {
	acceptAppointmentRequest,
	rejectAppointmentRequest,
} from '../api/portal';

export default {
	components: {
		Modal: () => import('../templates/others/modal'),
	},
	computed: {
		...mapGetters(['showAppointmentData']),
		...mapGetters('help', ['selectEntities']),
		...mapGetters('appointment', ['showSelectedAppointment']),
	},
	methods: {
		formatDate(dateString) {
			if (!dateString) return '-';
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', {
				year: 'numeric',
				month: 'short',
				day: 'numeric',
			});
		},
		getIntermediaryData(ref) {
			let name = '';
			let email = '';

			const agent = this.selectEntities.find(
				(entity) => entity.intermediarySystemRef === ref,
			);

			if (agent) {
				if (agent.intermediaryNames) {
					name = agent.intermediaryNames;
				}
				if (agent.primaryEmail) {
					email = agent.primaryEmail;
				}
			}

			return { name, email };
		},

		async handleAcceptAppointment() {
			try {
				const res = await acceptAppointmentRequest({
					appointmentSystemRef:
						this.showSelectedAppointment.appointmentSystemRef,
				});
				if (res.status) {
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Appointment accepted',
						duration: 3000,
					});
				}
			} catch (err) {
				const errMsg =
					err.response.data.message ||
					err.response.data.errors ||
					'An error occured accepting';
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: errMsg,
					duration: 3000,
				});
			}
		},
		async handleRejectAppointment() {
			try {
				const res = await rejectAppointmentRequest({
					appointmentSystemRef:
						this.showSelectedAppointment.appointmentSystemRef,
					remarks: 'Appointment reject',
				});
				if (res.status) {
					Event.$emit('notifications:show', {
						type: 'success',
						title: 'Success',
						message: 'Appointment accepted',
						duration: 3000,
					});
				}
			} catch (err) {
				const errMsg =
					err.response.data.message ||
					err.response.data.errors ||
					'An error occured accepting';
				Event.$emit('notifications:show', {
					type: 'error',
					title: 'Error',
					message: errMsg,
					duration: 3000,
				});
			}
		},
	},
};
</script>
